import React, { useState, useEffect } from 'react'
import { useResume } from './ResumeProvider'
import { Textarea } from '~/lib/ui/textarea'
import { Button } from '~/lib/ui/button'
import { Toaster } from '~/lib/ui/toast'
import {
  TabNav,
  TabPanel,
  PanelContainer,
  ButtonContainer,
  TabButton,
  setTabNavActiveTab,
} from '~/components/shared/TabNav'
import {
  DocumentMagnifyingGlassIcon,
  IdentificationIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline'
import Editor from './Editor/index'
import PdfViewerPanel from './PdfViewerPanel'
import { StyleEditor, useTemplate } from '~/lib/pdf'
import ContextWrapper from './ContextWrapper'
import SelectTemplatePanel, { TemplatePreview } from './SelectTemplatePanel'
import useTranslation, {
  TranslationProvider,
} from '~/components/shared/TranslationProvider'
import translations from './locales.json'

const ResumeBuilder = ({ locale, ...props }) => {
  return (
    <TranslationProvider
      locale={locale}
      translations={translations}
      scope={'javascript.components.ResumeBuilder'}
    >
      <ContextWrapper locale={locale} {...props}>
        <ResumeBuilderInner />
      </ContextWrapper>
    </TranslationProvider>
  )
}
const ResumeBuilderInner = () => {
  const { t } = useTranslation()
  const { templateIndex } = useTemplate()
  const {
    devMode,
    documentId,
    previewTemplateIndex,
    setPreviewTemplateIndex,
    uncategorizedData,
    resumeName,
  } = useResume()

  const defaultActiveTab = documentId ? 'details' : 'chooseTemplate'
  const uncategorizedDataExists = Object.keys(uncategorizedData).length > 0
  const onChangeActiveTab = (name) => {
    if (name == 'chooseTemplate') {
      setPreviewTemplateIndex(templateIndex)
    } else {
      setPreviewTemplateIndex(null)
    }
  }

  return (
    <div
      className="flex w-full flex-col md:flex-row"
      data-testid="resume-builder-main"
    >
      {/* CTS-9271 — page-has-heading-one: editor view has no h1 */}
      <h1 className="sr-only">{resumeName || t('new_resume')}</h1>
      <div className="md:basis-3/5">
        <TabNav
          className="px-4 pt-4 lg:px-8 xl:px-12"
          defaultActiveTab={defaultActiveTab}
          onChangeActiveTab={onChangeActiveTab}
          mobileMode="swipe"
        >
          <ButtonContainer>
            {uncategorizedDataExists && (
              <TabButton
                name="importResult"
                text={t('import_result')}
                Icon={DocumentTextIcon}
              />
            )}
            <TabButton
              name="chooseTemplate"
              text={t('choose_template')}
              Icon={DocumentMagnifyingGlassIcon}
            />
            <TabButton
              name="details"
              text={t('details')}
              Icon={IdentificationIcon}
            />
            <TabButton
              name="formattingAndDesign"
              text={t('formatting_design')}
              Icon={ClipboardDocumentCheckIcon}
            />
            {devMode && (
              <TabButton
                name="text"
                text={t('raw_json')}
                Icon={DocumentTextIcon}
              />
            )}
          </ButtonContainer>
          <PanelContainer className="md:min-h-[91vh]">
            <TabPanel name="chooseTemplate" className="bg-[#E0F2F9] pt-8">
              <SelectTemplatePanel />
            </TabPanel>
            <TabPanel name="details" className="pt-8">
              <Editor />
            </TabPanel>
            <TabPanel name="formattingAndDesign" className="pt-8">
              <StyleEditor />
            </TabPanel>
            <TabPanel name="text" className="pt-8">
              <TextEditor />
            </TabPanel>
            <TabPanel name="importResult" className="pt-8">
              <ImportResultPanel />
            </TabPanel>
          </PanelContainer>
        </TabNav>
      </div>
      <div id="resume-pdf-preview" className="relative md:flex-grow">
        <div className="w-full md:sticky md:left-0 md:top-0 md:h-screen">
          {previewTemplateIndex === null ? (
            <PdfViewerPanel />
          ) : (
            <RightPanelTemplatePreview />
          )}
        </div>
      </div>
      <Toaster />
    </div>
  )
}
export default ResumeBuilder

const RightPanelTemplatePreview = () => {
  const { t } = useTranslation()
  const { previewTemplateIndex } = useResume()
  const { setTemplateIndex } = useTemplate()
  const selectTemplateClick = () => {
    setTemplateIndex(previewTemplateIndex)
    setTabNavActiveTab('details')
  }

  return (
    <div className="flex flex-col bg-[#dadfe3] px-6 pt-8 md:h-full md:max-w-[35vw]">
      <div className="mb-6 flex flex-row items-center justify-around">
        <Button variant="default" onClick={selectTemplateClick}>
          {t('select_template')}
        </Button>
      </div>
      <TemplatePreview
        templateIndex={previewTemplateIndex}
        className="rounded-lg"
      />
    </div>
  )
}
const ImportResultPanel = () => {
  const { t } = useTranslation()
  const { uncategorizedData } = useResume()
  const [displayUncategorizedData, setDisplayUncategorizedData] = useState(
    JSON.stringify(uncategorizedData, null, 2).replace(/(?:\\\\n|\\\\t)/g, ' '),
  )

  return (
    <div className="flex h-full flex-col">
      <div>{t('analysis_were_extracted')}</div>
      <Textarea
        className="h-full w-full"
        value={displayUncategorizedData}
        onChange={(e) => setDisplayUncategorizedData(e.target.value)}
        disabled
      ></Textarea>
    </div>
  )
}

const TextEditor = () => {
  const { t } = useTranslation()
  const { resumeDocument, setResumeDocument } = useResume()
  const [editorResume, setEditorResume] = useState(
    JSON.stringify(resumeDocument, null, 2),
  )

  const sync = () => {
    const updatedResume = JSON.parse(editorResume)
    setResumeDocument(updatedResume)
  }

  useEffect(() => {
    setEditorResume(JSON.stringify(resumeDocument, null, 2))
  }, [resumeDocument])

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-row justify-evenly">
        <Button onClick={sync}>{t('sync')}</Button>
      </div>
      <Textarea
        className="h-full w-full"
        value={editorResume}
        onChange={(e) => setEditorResume(e.target.value)}
      ></Textarea>
    </div>
  )
}
